import { useCallback, useEffect, useRef, useState } from 'react';
import { localDay } from '../../shared/date.js';
import { mergeProviderIntake, parseDeterministicIntake } from '../../shared/intake.js';
import { api, recordFunnelEvent } from '../api.js';
import type { IntakeDraftExtraction, ReportDraft } from '../state/draft.js';
import { decodeRealtimeEvent } from './realtime-events.js';

type VoiceState =
  'idle' | 'connecting' | 'listening' | 'thinking' | 'complete' | 'denied' | 'error';

interface RealtimeExtractionArguments {
  itemDescription?: string;
  category?: ReportDraft['item']['category'];
  color?: ReportDraft['item']['color'];
  brand?: string;
  distinguishingFeatures?: string;
  dateKind?: 'KNOWN' | 'UNKNOWN';
  dateStart?: string;
  dateEnd?: string;
  dateInFuture?: boolean;
  placeKind?: 'KNOWN' | 'UNKNOWN';
  placeName?: string;
  placeContext?: string;
  confidence?: number;
  walletPolicyRequired?: boolean;
}

export function useRealtimeIntake(input: {
  language: 'en';
  onExtraction: (extraction: IntakeDraftExtraction) => void;
}) {
  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState<ReportDraft['transcript']>([]);
  const [assistantDraft, setAssistantDraft] = useState('');
  const [clarificationCount, setClarificationCount] = useState(0);
  const clarificationCountRef = useRef(0);
  const peer = useRef<RTCPeerConnection | null>(null);
  const channel = useRef<RTCDataChannel | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const remoteAudio = useRef<HTMLAudioElement | null>(null);
  const timeout = useRef<number | null>(null);
  const extracted = useRef(false);
  const startedAt = useRef(0);
  const userText = useRef<string[]>([]);
  const assistantText = useRef<string[]>([]);
  const assistantBuffer = useRef('');
  const userHasSpoken = useRef(false);

  const stop = useCallback((nextState: VoiceState = 'idle') => {
    if (timeout.current) window.clearTimeout(timeout.current);
    timeout.current = null;
    channel.current?.close();
    channel.current = null;
    peer.current?.close();
    peer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (remoteAudio.current) {
      remoteAudio.current.pause();
      remoteAudio.current.srcObject = null;
    }
    remoteAudio.current = null;
    setState(nextState);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const handleMessage = useCallback(
    (raw: string) => {
      let payload: unknown;
      try {
        payload = JSON.parse(raw) as unknown;
      } catch {
        return;
      }
      const event = decodeRealtimeEvent(payload);
      if (!event) return;
      if (event.kind === 'USER_TRANSCRIPT') {
        userHasSpoken.current = true;
        userText.current.push(event.text);
        setTranscript((current) => [...current, { role: 'USER', text: event.text }]);
        setState('thinking');
      } else if (event.kind === 'ASSISTANT_DELTA') {
        assistantBuffer.current += event.text;
        setAssistantDraft(assistantBuffer.current);
      } else if (event.kind === 'ASSISTANT_DONE') {
        const text = event.text || assistantBuffer.current;
        assistantBuffer.current = '';
        setAssistantDraft('');
        if (text) {
          assistantText.current.push(text);
          setTranscript((current) => [...current, { role: 'ASSISTANT', text }]);
          if (userHasSpoken.current && text.includes('?')) {
            setClarificationCount((current) => {
              const next = Math.min(2, current + 1);
              clarificationCountRef.current = next;
              if (next >= 2 && channel.current?.readyState === 'open') {
                channel.current.send(
                  JSON.stringify({
                    type: 'session.update',
                    session: {
                      type: 'realtime',
                      tool_choice: 'required',
                      instructions:
                        'After the guest answers this question, do not ask another question. Call submit_intake_extraction exactly once with explicit UNKNOWN values where needed.',
                    },
                  }),
                );
              }
              return next;
            });
          }
        }
        setState('listening');
      } else if (event.kind === 'EXTRACTION' && !extracted.current) {
        extracted.current = true;
        let argumentsValue: RealtimeExtractionArguments;
        try {
          argumentsValue = JSON.parse(event.arguments) as RealtimeExtractionArguments;
        } catch {
          stop('error');
          return;
        }
        const extraction = mapExtraction(
          argumentsValue,
          input.language,
          userText.current.join(' '),
          assistantText.current.join(' '),
          clarificationCountRef.current,
        );
        input.onExtraction(extraction);
        recordFunnelEvent({
          type: 'VOICE_STARTED',
          step: 'INTAKE',
          durationMs: Date.now() - startedAt.current,
          source: 'VOICE',
          language: input.language,
        });
        stop('complete');
      } else if (event.kind === 'ERROR') {
        stop('error');
      }
    },
    [input, stop],
  );

  const start = useCallback(async () => {
    stop('connecting');
    setTranscript([]);
    setAssistantDraft('');
    setClarificationCount(0);
    clarificationCountRef.current = 0;
    extracted.current = false;
    userText.current = [];
    assistantText.current = [];
    assistantBuffer.current = '';
    userHasSpoken.current = false;
    startedAt.current = Date.now();
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (error) {
      const denied =
        error instanceof DOMException &&
        ['NotAllowedError', 'PermissionDeniedError'].includes(error.name);
      setState(denied ? 'denied' : 'error');
      if (denied)
        recordFunnelEvent({ type: 'MICROPHONE_DENIED', step: 'INTAKE', language: input.language });
      return;
    }

    try {
      const secret = await api<{ value: string; expiresAt: number }>(
        '/api/realtime/client-secret',
        { method: 'POST' },
      );
      const pc = new RTCPeerConnection();
      peer.current = pc;
      const audio = document.createElement('audio');
      audio.autoplay = true;
      remoteAudio.current = audio;
      pc.ontrack = (event) => {
        audio.srcObject = event.streams[0] ?? null;
      };
      const track = stream.current.getAudioTracks()[0];
      if (!track) throw new Error('No audio track');
      pc.addTrack(track, stream.current);
      const dc = pc.createDataChannel('oai-events');
      channel.current = dc;
      dc.addEventListener('message', (event) => handleMessage(String(event.data)));
      dc.addEventListener('open', () => {
        setState('listening');
        dc.send(
          JSON.stringify({
            type: 'response.create',
            response: {
              instructions:
                'Give one short greeting and ask the guest to say what they lost, when, and where.',
            },
          }),
        );
      });
      dc.addEventListener('close', () =>
        setState((current) => (current === 'complete' ? current : 'error')),
      );
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const answer = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        body: offer.sdp ?? '',
        headers: { authorization: `Bearer ${secret.value}`, 'content-type': 'application/sdp' },
      });
      if (!answer.ok) throw new Error('Realtime SDP exchange failed');
      await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() });
      timeout.current = window.setTimeout(() => stop('error'), 120_000);
    } catch {
      stop('error');
    }
  }, [handleMessage, input.language, stop]);

  return { state, transcript, assistantDraft, clarificationCount, start, restart: start, stop };
}

function mapExtraction(
  value: RealtimeExtractionArguments,
  language: 'en',
  userText: string,
  assistantText: string,
  clarificationCount: number,
): IntakeDraftExtraction {
  const deterministic = parseDeterministicIntake(userText, language);
  const merged = mergeProviderIntake(
    deterministic,
    { ...value },
    deterministic.redactedText,
    localDay(new Date()),
  );
  return {
    source: 'VOICE',
    text: deterministic.redactedText || 'Voice report',
    ...(assistantText ? { assistantText } : {}),
    item: {
      ...merged.item,
      description: value.itemDescription?.trim() || merged.item.description || 'Unspecified item',
      distinguishingFeatures:
        value.distinguishingFeatures?.trim() || merged.item.distinguishingFeatures || '',
    },
    lostDate: merged.lostDate,
    dateInFuture: merged.dateInFuture,
    place: merged.place,
    walletPolicyRequired: merged.walletPolicyRequired,
    confidence: merged.confidence,
    clarificationCount: Math.min(2, clarificationCount),
    extractionToolCallCount: 1,
  };
}
